import type { MediaDocType, MediaLinkDocType } from '@ast-data/shared';
import { pairId } from '../../utils/deterministicId';

/**
 * A picture shown in one place: its own media doc, and the link that brings it
 * there — null where this place is its origin.
 */
export interface ShownMedia {
    media: MediaDocType;
    link: MediaLinkDocType | null;
}

/** The fields a link, a media doc and an owner share: where something hangs. */
export interface MediaPlace {
    ownerType: string;
    ownerId: string;
}

/**
 * The id of the link showing `mediaId` in `ownerId`. Derived rather than drawn,
 * so linking the same picture twice — on two offline devices, say — lands on
 * ONE document instead of two.
 */
export function mediaLinkId(mediaId: string, ownerId: string): string {
    return pairId(ownerId, mediaId);
}

/** The photo a sketch was drawn over, or '' when it has none. */
export function sceneBackgroundId(scene: string): string {
    if (!scene) {
        return '';
    }
    try {
        const background = (JSON.parse(scene) as { bg?: string }).bg;
        return typeof background === 'string' ? background : '';
    } catch {
        return '';
    }
}

/**
 * Everything one owner shows: its own pictures, then the linked ones, each
 * group oldest first. A link whose media doc is not (yet) on this device is
 * left out rather than shown as a hole.
 */
export function mediaShownBy(
    ownMedia: readonly MediaDocType[],
    linksHere: readonly MediaLinkDocType[],
    linkedMedia: readonly MediaDocType[]
): ShownMedia[] {
    const byCreation = (first: MediaDocType, second: MediaDocType) =>
        first.createdAt.localeCompare(second.createdAt);
    const ownIds = new Set(ownMedia.map((media) => media.id));
    const linkByMediaId = new Map(
        linksHere.map((link) => [link.mediaId, link])
    );
    const own = [...ownMedia]
        .sort(byCreation)
        .map((media) => ({ media, link: null }));
    const linked = [...linkedMedia]
        .filter((media) => !ownIds.has(media.id))
        .sort(byCreation)
        .flatMap((media) => {
            const link = linkByMediaId.get(media.id);
            return link ? [{ media, link }] : [];
        });
    return [...own, ...linked];
}

/**
 * The photos a gallery hides because a sketch shown in the same place was drawn
 * over them — the sketch shows the photo already, as one of its layers.
 */
export function coveredPhotoIds(shown: readonly MediaDocType[]): Set<string> {
    return new Set(
        shown
            .filter((media) => media.kind === 'sketch')
            .map((media) => sceneBackgroundId(media.scene))
            .filter(Boolean)
    );
}

/**
 * A choice of pictures plus the photos under the chosen sketches. A sketch and
 * the photo it was drawn on are ONE picture to whoever made it, so they are
 * linked and unlinked together — a sketch arriving without its photo would
 * leave the photo to show up on its own wherever the sketch is not.
 */
export function withBackgroundPhotos(
    mediaIds: readonly string[],
    mediaById: ReadonlyMap<string, MediaDocType>
): string[] {
    const ids = new Set(mediaIds);
    for (const id of mediaIds) {
        const media = mediaById.get(id);
        const background =
            media?.kind === 'sketch' ? sceneBackgroundId(media.scene) : '';
        if (background && mediaById.has(background)) {
            ids.add(background);
        }
    }
    return [...ids];
}

/**
 * Whether deleting this sketch takes its background photo along. Only when the
 * photo is the sketch's own — same origin — and shows nowhere the sketch does
 * not: a photo someone linked on its own, before it was drawn over, is still
 * that other description's picture.
 */
export function backgroundGoesWithSketch(
    sketch: MediaDocType,
    photo: MediaDocType | undefined,
    links: readonly MediaLinkDocType[]
): boolean {
    if (!photo || photo.ownerId !== sketch.ownerId) {
        return false;
    }
    const sketchPlaces = new Set(
        links
            .filter((link) => link.mediaId === sketch.id)
            .map((link) => link.ownerId)
    );
    return links
        .filter((link) => link.mediaId === photo.id)
        .every((link) => sketchPlaces.has(link.ownerId));
}

/** A picture whose origin goes away but that lives on in a linked place. */
export interface MediaMove {
    mediaId: string;
    /** The new origin: the oldest surviving link's owner. */
    ownerType: string;
    ownerId: string;
    projectId: string;
    /** That link, now redundant — the media doc itself points there. */
    linkId: string;
}

export interface OwnerRemovalPlan {
    /** Pictures that show nowhere else any more: deleted. */
    deleteMediaIds: string[];
    /** Pictures that move to a surviving place. */
    moves: MediaMove[];
    /** Links that go: every link INTO a going owner, plus each move's own. */
    deleteLinkIds: string[];
    /** The owners that go, whose picture groups go with them. */
    goingOwnerIds: string[];
}

/**
 * What happens to the pictures when these owners are deleted.
 *
 * A picture whose origin goes but which is linked somewhere that stays is not
 * lost: its oldest surviving link becomes its new origin. Only a picture shown
 * nowhere else goes with its owner — and with it every link it still has. The
 * links into the going owners go too.
 *
 * A link only counts as surviving when its description is in `liveOwnerIds`:
 * one left pointing at a description deleted some other way would hand the
 * picture to nobody, and it would vanish from every screen.
 *
 * `ownerKey` is `ownerType:ownerId`, the same key both sides are matched by.
 */
export function planOwnerRemoval(
    goingOwnerKeys: ReadonlySet<string>,
    media: readonly (MediaPlace & { id: string })[],
    links: readonly MediaLinkDocType[],
    liveOwnerIds: ReadonlySet<string>
): OwnerRemovalPlan {
    const key = (place: MediaPlace) => `${place.ownerType}:${place.ownerId}`;
    const deleteLinkIds = new Set(
        links
            .filter((link) => goingOwnerKeys.has(key(link)))
            .map((link) => link.id)
    );
    const linksByMedia = new Map<string, MediaLinkDocType[]>();
    for (const link of links) {
        linksByMedia.set(link.mediaId, [
            ...(linksByMedia.get(link.mediaId) ?? []),
            link
        ]);
    }

    const deleteMediaIds: string[] = [];
    const moves: MediaMove[] = [];
    for (const item of media) {
        if (!goingOwnerKeys.has(key(item))) {
            continue;
        }
        const itemLinks = linksByMedia.get(item.id) ?? [];
        const heir = itemLinks
            .filter(
                (link) =>
                    !deleteLinkIds.has(link.id) &&
                    liveOwnerIds.has(link.ownerId)
            )
            .sort(
                (firstLink, secondLink) =>
                    firstLink.createdAt.localeCompare(secondLink.createdAt) ||
                    firstLink.id.localeCompare(secondLink.id)
            )[0];
        if (!heir) {
            deleteMediaIds.push(item.id);
            for (const link of itemLinks) {
                deleteLinkIds.add(link.id);
            }
            continue;
        }
        moves.push({
            mediaId: item.id,
            ownerType: heir.ownerType,
            ownerId: heir.ownerId,
            projectId: heir.projectId,
            linkId: heir.id
        });
        deleteLinkIds.add(heir.id);
    }
    return {
        deleteMediaIds,
        moves,
        deleteLinkIds: [...deleteLinkIds],
        goingOwnerIds: [...goingOwnerKeys].map((ownerKey) =>
            ownerKey.slice(ownerKey.indexOf(':') + 1)
        )
    };
}

/**
 * Take a picture out of its ORIGIN only, where it is linked elsewhere: the
 * oldest link into a description that still exists becomes the new origin,
 * and every other link stays as it was. Null when there is no such link — then
 * the only way to remove it here is to delete it.
 *
 * A sketch's own background photo moves along to the same place, as the pair
 * it is; the photo's link there, if it had one, is now redundant.
 */
export function planDetachFromOrigin(
    media: MediaDocType,
    backgroundPhoto: MediaDocType | undefined,
    links: readonly MediaLinkDocType[],
    liveOwnerIds: ReadonlySet<string>
): OwnerRemovalPlan | null {
    const heir = links
        .filter(
            (link) =>
                link.mediaId === media.id && liveOwnerIds.has(link.ownerId)
        )
        .sort(
            (firstLink, secondLink) =>
                firstLink.createdAt.localeCompare(secondLink.createdAt) ||
                firstLink.id.localeCompare(secondLink.id)
        )[0];
    if (!heir) {
        return null;
    }
    const moveTo = (item: MediaDocType, linkId: string): MediaMove => ({
        mediaId: item.id,
        ownerType: heir.ownerType,
        ownerId: heir.ownerId,
        projectId: heir.projectId,
        linkId
    });
    const moves = [moveTo(media, heir.id)];
    const deleteLinkIds = [heir.id];
    if (backgroundPhoto && backgroundPhoto.ownerId === media.ownerId) {
        const photoLinkThere = links.find(
            (link) =>
                link.mediaId === backgroundPhoto.id &&
                link.ownerId === heir.ownerId
        );
        moves.push(moveTo(backgroundPhoto, photoLinkThere?.id ?? ''));
        if (photoLinkThere) {
            deleteLinkIds.push(photoLinkThere.id);
        }
    }
    return { deleteMediaIds: [], moves, deleteLinkIds, goingOwnerIds: [] };
}
